import { createContext, useCallback, useEffect, useState } from "react";
import { getProfile } from "../api/auth";
import { getMyProfile } from "../api/usersApi";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Extended profile (UserProfile + nested User) — holds avatar_url, country,
  // display_currency, etc. Loaded after auth check so the navbar can render
  // a real photo without each page fetching its own profile.
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const refreshProfile = useCallback(async () => {
    try {
      const data = await getMyProfile();
      setProfile(data);
      return data;
    } catch (err) {
      // Profile fetch can fail with 401 if the token expired between checks.
      // Don't blow up auth state — let the user keep using the app.
      console.error("Profile refresh failed:", err);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("access");

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await getProfile(token);
      setUser(response.data);
      setIsAuthenticated(true);
      // Best-effort profile fetch — don't gate auth on it.
      await refreshProfile();
    } catch (error) {
      console.error("Auth check failed:", error);
      await refreshToken();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshProfile]);

  // Check if user is logged in on app load
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refreshToken = async () => {
    const refresh = localStorage.getItem("refresh");

    if (!refresh) {
      logout();
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/token/refresh/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("access", data.access);
        if (data.refresh) {
          localStorage.setItem("refresh", data.refresh);
        }
        await checkAuth();
      } else {
        logout();
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
    }
  };

  const login = (userData, tokens) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem("access", tokens.access);
    localStorage.setItem("refresh", tokens.refresh);
    // Pull the full profile so the navbar avatar etc. are correct right away.
    refreshProfile();
  };

  const logout = () => {
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        isAuthenticated,
        loading,
        login,
        logout,
        checkAuth,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
